/**
 * Button Design System — SINGLE SOURCE OF TRUTH
 *
 * Use these constants for ALL buttons across the app. Do not write ad-hoc
 * button classNames with `bg-primary-500 text-white`, `bg-primary-600`, etc.
 * Those bright-cyan brand colors do NOT have enough contrast with white text.
 *
 * Why this file exists: we kept making the same accessibility mistake
 * (white text on bright primary-500 cyan = unreadable). The `btn.primary`
 * variant uses `primary-700` (#009d89) which has proper contrast with white.
 *
 * If you find yourself writing `bg-primary-500 text-white` — STOP. Use
 * `btn.primary.md` (or another variant) instead.
 *
 * Five variants, three sizes each:
 *
 *   primary   — primary CTA (save, confirm, classify, "Add Task")
 *   secondary — outlined secondary action
 *   ghost     — low-emphasis (cancel, dismiss, expand)
 *   danger    — destructive (disconnect, delete, revoke)
 *   inline    — utility button inside a row (copy, open link)
 *
 * Sizes:
 *   md (default) — py-2 px-4 text-sm — standard button in a card
 *   sm           — py-1.5 px-3 text-sm — compact, in a row of controls
 *   xs           — py-1 px-2.5 text-xs — inside a table row or tag
 */

const BASE =
  'inline-flex items-center justify-center gap-1.5 font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer';

export const btn = {
  primary: {
    md: `${BASE} px-4 py-2 text-sm bg-primary-700 hover:bg-primary-800 text-white`,
    sm: `${BASE} px-3 py-1.5 text-sm bg-primary-700 hover:bg-primary-800 text-white`,
    xs: `${BASE} px-2.5 py-1 text-xs bg-primary-700 hover:bg-primary-800 text-white`,
  },

  secondary: {
    md: `${BASE} px-4 py-2 text-sm border border-border-subtle-light dark:border-border-subtle-dark bg-surface-raised-light dark:bg-surface-raised-dark text-text-primary-light dark:text-text-primary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark`,
    sm: `${BASE} px-3 py-1.5 text-sm border border-border-subtle-light dark:border-border-subtle-dark bg-surface-raised-light dark:bg-surface-raised-dark text-text-secondary-light dark:text-text-secondary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark`,
    xs: `${BASE} px-2.5 py-1 text-xs border border-border-subtle-light dark:border-border-subtle-dark bg-surface-raised-light dark:bg-surface-raised-dark text-text-secondary-light dark:text-text-secondary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark`,
  },

  ghost: {
    md: `${BASE} px-4 py-2 text-sm text-text-secondary-light dark:text-text-secondary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark`,
    sm: `${BASE} px-3 py-1.5 text-sm text-text-secondary-light dark:text-text-secondary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark`,
    xs: `${BASE} px-2.5 py-1 text-xs text-text-secondary-light dark:text-text-secondary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark`,
  },

  danger: {
    md: `${BASE} px-4 py-2 text-sm text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20`,
    sm: `${BASE} px-3 py-1.5 text-sm text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20`,
    xs: `${BASE} px-2.5 py-1 text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20`,
  },

  inline: {
    md: `${BASE} px-3 py-2 text-sm border border-border-subtle-light dark:border-border-subtle-dark text-text-secondary-light dark:text-text-secondary-dark bg-surface-raised-light dark:bg-surface-raised-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark flex-shrink-0`,
    sm: `${BASE} px-2.5 py-1.5 text-xs border border-border-subtle-light dark:border-border-subtle-dark text-text-secondary-light dark:text-text-secondary-dark bg-surface-raised-light dark:bg-surface-raised-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark flex-shrink-0`,
  },
} as const;
